<script setup lang="ts">
import DateRangePicker from '@/shared/components/form/DateRangePicker.vue'
import InternalEmployerSelect from '@/shared/components/form/InternalEmployerSelect.vue'
import {
  buildForecastRangePresets,
  endOfQuarter,
  getContractStatusLabel,
  getContractStatusSeverity,
  getStatusLabel,
  getStatusSeverity,
  startOfQuarter,
  toDateOnlyString,
} from '@/features/sales/composables/useSalesMetricsAdapter'
import { useAdminGetSalesForecast } from '@swipe4work/api-orval'
import type {
  AdminGetSalesForecastParams,
  GetSalesForecastForecastDealDto,
} from '@swipe4work/models-orval'

definePage({
  path: '/sales/sales-metrics/forecast',
  name: '/sales/SalesMetrics/forecast',
  meta: {
    permission: {
      resource: 'SalesMetrics',
      level: 'Read',
    },
  },
})

type ForecastFilterState = {
  period: {
    from: Date | null
    to: Date | null
  } | null
  salesPersonId: number | null
  onlyDeviating: boolean
}

const presets = buildForecastRangePresets()
const today = new Date()

const {
  filters,
  searchInput,
  search,
  offset,
  rows,
  ROWS_PER_PAGE_OPTIONS,
  onPage,
} = useDataTableFilters<ForecastFilterState>({
  schema: {
    period: 'dateRange',
    salesPersonId: 'id',
    onlyDeviating: 'boolean',
  },
  defaultFilters: () => ({
    period: {
      from: startOfQuarter(today),
      to: endOfQuarter(today),
    },
    salesPersonId: null,
    onlyDeviating: false,
  }),
  defaultPageSize: 10,
})

// The filters sit inline instead of in a panel, so changing one has to rewind the paging itself.
watch(
  () => [
    filters.period?.from,
    filters.period?.to,
    filters.salesPersonId,
    filters.onlyDeviating,
  ],
  () => {
    offset.value = 0
  },
)

const params = computed<AdminGetSalesForecastParams>(() => ({
  dateFrom: filters.period?.from ? (toDateOnlyString(filters.period.from) as unknown as Date) : undefined,
  dateTo: filters.period?.to ? (toDateOnlyString(filters.period.to) as unknown as Date) : undefined,
  salesPersonId: filters.salesPersonId ?? undefined,
  search: search.value,
  onlyDeviating: filters.onlyDeviating || undefined,
  limit: rows.value,
  offset: offset.value,
}))

const queryEnabled = computed(() => Boolean(params.value.dateFrom && params.value.dateTo))

const { data, isPending, isError } = useAdminGetSalesForecast(params, {
  query: { enabled: queryEnabled },
})

const result = computed(() => data.value)
const deals = computed<GetSalesForecastForecastDealDto[]>(() => result.value?.deals ?? [])
const totalCount = computed(() => result.value?.totalCount ?? 0)

const eurFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const formatCents = (cents: number | null | undefined) =>
  cents == null ? '—' : eurFormatter.format(cents / 100)

const contractedValue = computed(() => formatCents(result.value?.totalContractedValueCents))
const averageContractedValue = computed(() => formatCents(result.value?.averageContractedValueCents))
const definitiveValue = computed(() => formatCents(result.value?.totalDefinitiveValueCents))
const currentArr = computed(() => formatCents(result.value?.currentArrCents))
const mrrContracted = computed(() => formatCents(result.value?.mrrContractedCents))

const periodEndLabel = computed(() =>
  filters.period?.to
    ? new Intl.DateTimeFormat('nl-NL', { dateStyle: 'short' }).format(filters.period.to)
    : '—',
)

/*
 * Contracted minus definitive only covers the deals carrying both numbers: a closed deal without a
 * contracted value still counts in the definitive total, but comparing it against a contracted €0
 * would inflate the difference.
 */
const contractedMinusDefinitive = computed(() =>
  (result.value?.contractedValueOfComparableDealsCents ?? 0)
  - (result.value?.definitiveValueOfComparableDealsCents ?? 0),
)

const deviatingCount = computed(() => result.value?.totalDeviatingDeals ?? 0)

function getContractedLabel(deal: GetSalesForecastForecastDealDto): string {
  if (!deal.contractedSubscriptionType) return '—'
  if (deal.contractedSubscriptionType === 'MatchCredits') {
    return `${deal.contractedMatchCredits ?? 0} match credits`
  }

  return deal.contractedSubscriptionTitle ?? 'Vacature Slots'
}

function getDefinitiveLabel(deal: GetSalesForecastForecastDealDto): string {
  if (deal.definitiveSubscriptionTitle) {
    return deal.definitiveSubscriptionTitle
  }
  if (deal.definitiveCreditPackageName || deal.definitiveMatchCredits != null) {
    const credits = deal.definitiveMatchCredits != null ? `${deal.definitiveMatchCredits} match credits` : null

    return [ deal.definitiveCreditPackageName, credits ].filter(Boolean).join(' — ')
  }

  return '—'
}

const formatDate = (date: Date | string | null | undefined) => {
  if (!date) return '—'

  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'short' }).format(new Date(date))
}
</script>

<template>
  <div class="space-y-4">
    <div class="flex flex-wrap items-end gap-3">
      <div class="flex flex-col gap-1">
        <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Periode</label>
        <DateRangePicker v-model="filters.period" :presets="presets" placeholder="Kies periode" />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Sales persoon</label>
        <InternalEmployerSelect v-model="filters.salesPersonId" placeholder="Alle sales personen" />
      </div>
    </div>

    <Message v-if="!queryEnabled" severity="info">
      Selecteer een periode om de forecast te zien.
    </Message>

    <template v-else>
      <div v-if="isPending" class="grid grid-cols-2 gap-4 lg:grid-cols-6 sm:grid-cols-3">
        <Skeleton v-for="n in 6" :key="n" height="5rem" />
      </div>

      <Message v-else-if="isError" severity="error">
        Er is iets misgegaan bij het laden van de forecast.
      </Message>

      <template v-else>
        <div class="grid grid-cols-2 gap-4 lg:grid-cols-6 sm:grid-cols-3">
          <StatTile
            label="Contracted deals"
            :value="String(result?.totalContractedDeals ?? 0)"
            :subtitle="`Contracted en nog niet gefactureerd op ${periodEndLabel}`"
          />
          <StatTile
            label="Subscription deals"
            :value="String(result?.totalContractedSubscriptionDeals ?? 0)"
          />
          <StatTile
            label="Match-credit deals"
            :value="String(result?.totalContractedMatchCreditDeals ?? 0)"
          />
          <StatTile
            label="Contracted waarde"
            :value="contractedValue"
          />
          <StatTile
            label="Gem. contracted waarde"
            :value="averageContractedValue"
            subtitle="Per subscription deal"
          />
          <StatTile
            label="Contracted match credits"
            :value="String(result?.totalContractedMatchCredits ?? 0)"
          />
        </div>

        <div class="grid grid-cols-2 gap-4 lg:grid-cols-6 sm:grid-cols-2">
          <StatTile
            label="Definitieve deals"
            :value="String(result?.totalDefinitiveDeals ?? 0)"
            subtitle="Deals met een definitief abonnement"
          />
          <StatTile
            label="Definitieve waarde"
            :value="definitiveValue"
            subtitle="Totale waarde van definitieve abonnementen"
          />
          <StatTile
            label="Contracted − definitief"
            :value="formatCents(contractedMinusDefinitive)"
            :subtitle="`Over ${result?.totalComparableDeals ?? 0} deals met beide waarden`"
          />
          <StatTile
            label="Huidige ARR"
            :value="currentArr"
            subtitle="Lopende abonnementen × 12 mnd, of de ARR override"
          />
          <StatTile
            label="Realized deals"
            :value="String(result?.totalRealizedDeals ?? 0)"
            subtitle="Eerste factuur verstuurd binnen de periode"
          />
          <StatTile
            label="MRR contracted"
            :value="mrrContracted"
            :subtitle="`Maandwaarde van de open contracted vacature-slot deals op ${periodEndLabel}`"
          />
        </div>

        <Card>
          <template #content>
            <div class="flex flex-col gap-4">
              <div class="flex flex-wrap items-center justify-between gap-3">
                <h3 class="text-surface-900 font-bold">
                  Contracted deals
                </h3>
                <div class="flex flex-wrap items-center gap-3">
                  <div class="flex items-center gap-2">
                    <ToggleSwitch v-model="filters.onlyDeviating" input-id="onlyDeviating" />
                    <label for="onlyDeviating" class="text-sm">
                      Afwijkende abonnementen
                      <span class="text-surface-400">({{ deviatingCount }})</span>
                    </label>
                  </div>

                  <IconField class="max-w-xs flex-1">
                    <InputIcon class="fa-regular fa-search" />
                    <InputText v-model="searchInput" placeholder="Zoeken..." class="w-full" />
                    <InputIcon
                      v-if="searchInput"
                      class="fa-regular fa-xmark cursor-pointer"
                      style="pointer-events: auto"
                      @click="searchInput = ''"
                    />
                  </IconField>
                </div>
              </div>

              <DataTable
                :value="deals"
                :lazy="true"
                :first="offset"
                :rows="rows"
                :rows-per-page-options="ROWS_PER_PAGE_OPTIONS"
                :total-records="totalCount"
                paginator
                paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                current-page-report-template="{first}-{last} van {totalRecords}"
                size="small"
                class="w-full"
                row-hover
                @page="onPage"
              >
                <template #empty>
                  <span class="text-sm text-surface-400">
                    {{ filters.onlyDeviating
                      ? 'Geen afwijkende abonnementen in deze periode.'
                      : 'Geen contracted deals in deze periode.' }}
                  </span>
                </template>
                <Column header="Bedrijf">
                  <template #body="{ data: deal }">
                    <span class="text-sm font-medium">{{ deal.companyName }}</span>
                  </template>
                </Column>
                <Column header="Sales persoon" style="width: 10rem">
                  <template #body="{ data: deal }">
                    <span class="text-sm">{{ deal.salesPersonName }}</span>
                  </template>
                </Column>
                <Column header="Status" style="width: 10rem">
                  <template #body="{ data: deal }">
                    <Tag :value="getStatusLabel(deal.status)" :severity="getStatusSeverity(deal.status)" />
                  </template>
                </Column>
                <Column header="Contract status" style="width: 9rem">
                  <template #body="{ data: deal }">
                    <Tag
                      :value="getContractStatusLabel(deal.contractStatus)"
                      :severity="getContractStatusSeverity(deal.contractStatus)"
                    />
                  </template>
                </Column>
                <Column header="Demo datum" style="width: 8rem">
                  <template #body="{ data: deal }">
                    {{ formatDate(deal.demoDate) }}
                  </template>
                </Column>
                <Column header="Onboarding datum" style="width: 10rem">
                  <template #body="{ data: deal }">
                    <div class="flex flex-col">
                      <span class="text-sm">
                        {{ formatDate(deal.onboardedAt ?? deal.potentialOnboardingDate) }}
                      </span>
                      <span v-if="!deal.onboardedAt" class="text-xs text-surface-400">
                        verwacht
                      </span>
                    </div>
                  </template>
                </Column>
                <Column header="Contracted abonnement" style="min-width: 10rem">
                  <template #body="{ data: deal }">
                    <span class="text-sm">{{ getContractedLabel(deal) }}</span>
                  </template>
                </Column>
                <Column header="Contracted waarde" style="width: 8rem">
                  <template #body="{ data: deal }">
                    <span class="text-sm font-mono">{{ formatCents(deal.contractedValueCents) }}</span>
                  </template>
                </Column>
                <Column header="Definitief abonnement" style="min-width: 10rem">
                  <template #body="{ data: deal }">
                    <div class="flex flex-col items-start gap-1">
                      <span
                        class="text-sm"
                        :class="getDefinitiveLabel(deal) === '—' ? 'text-surface-400' : 'text-green-500 font-medium'"
                      >
                        {{ getDefinitiveLabel(deal) }}
                      </span>
                      <Tag v-if="deal.isDeviating" value="Afwijkend" severity="warn" />
                    </div>
                  </template>
                </Column>
                <Column header="Definitieve waarde" style="width: 8rem">
                  <template #body="{ data: deal }">
                    <span class="text-sm font-mono">{{ formatCents(deal.definitiveValueCents) }}</span>
                  </template>
                </Column>
              </DataTable>
            </div>
          </template>
        </Card>
      </template>
    </template>
  </div>
</template>
